import { TFunction } from 'i18next';

import BookingOrder from '../types/BookingOrder';
import { CourseInstanceStatus } from '../../courseInstance/types/CourseInstance';
import User from '../../user/types/User';
import { getSearchParam } from '../../utils/searchParams';
import { SearchParamKeys } from '../views/BookingOrders';
import { BookingOrderApiFilter } from '../api/BookingOrderAPI';

export const getInfoText = (bookingOrder: BookingOrder, t: TFunction) => {
  const textList: string[] = [];
  const courseInstances = bookingOrder.courseInstances?.filter(
    (courseInstance) => courseInstance.status !== CourseInstanceStatus.Canceled,
  );

  if (!courseInstances || courseInstances?.length === 0) {
    textList.push(t('utils.bookingOrder.missingCourseInstances'));
  } else {
    const missingInstructor = courseInstances
      .map((courseInstance) => courseInstance.instructor)
      .includes(null);

    if (missingInstructor) {
      textList.push(t('utils.bookingOrder.missingInstructor'));
    }

    const missingTime =
      courseInstances.filter((courseInstance) => !courseInstance.startsAt)
        .length > 0;

    if (missingTime) {
      textList.push(t('utils.bookingOrder.missingTime'));
    }
  }
  if (!bookingOrder.invoiceRecipient) {
    textList.push(t('utils.bookingOrder.missingInvoiceRecipient'));
  }

  return textList.length > 0 ? textList : undefined;
};

export const shouldShowClientCompanyElsaMappingInformation = (
  currentUser: User | null,
  isElsaIntegrationEnabled?: boolean,
  bookingOrder?: BookingOrder,
) => {
  if (!isElsaIntegrationEnabled) {
    // Globally disabled
    return false;
  }

  if (!!bookingOrder?.subsidiaryCompany?.elsaSubsidiaryCompanyId) {
    // If subsidiary is mapped to ELSA and booking order is saved
    return !!bookingOrder.id;
  }

  if (!bookingOrder) {
    // Don't show ELSA mapping until booking saved
    return false;
  }
  return false;
};

export const getBookingOrderFilterFromSearchParams = (
  searchParams: URLSearchParams,
): BookingOrderApiFilter => {
  const from = getSearchParam(searchParams, SearchParamKeys.From, 'moment');
  const to = getSearchParam(searchParams, SearchParamKeys.To, 'moment');

  return {
    from: from?.toISOString(),
    to: to?.toISOString(),
    subsidiaryCompanyIds: getSearchParam(
      searchParams,
      SearchParamKeys.SubsidiaryCompanyIds,
      'list',
    )?.map(Number),
    courseIds: getSearchParam(
      searchParams,
      SearchParamKeys.CourseIds,
      'list',
    )?.map(Number),
    instructorIds: getSearchParam(
      searchParams,
      SearchParamKeys.InstructorIds,
      'list',
    )?.map((value) => (value === 'null' ? null : Number(value))),
    responsibleBookerIds: getSearchParam(
      searchParams,
      SearchParamKeys.ResponsibleBookerIds,
      'list',
    )?.map((value) => (value === 'null' ? null : Number(value))),
    invoiceRecipientIds: getSearchParam(
      searchParams,
      SearchParamKeys.InvoiceRecipientIds,
      'list',
    )?.map((value) => (value === 'null' ? null : Number(value))),
    isInvoiced: getSearchParam(
      searchParams,
      SearchParamKeys.IsInvoiced,
      'bool',
    ),
    clientCompanyIds: getSearchParam(
      searchParams,
      SearchParamKeys.ClientCompanyIds,
      'list',
    )?.map(Number),
  };
};
